import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnalyticsEvents, trackEvent } from '../analytics/events';
import { DEFAULT_GRID_SORT } from '../constants';
import { emptyFilter, RiftboundFilter } from '../types';
import {
  parseCollectionLanguage,
  type CollectionLanguageScope,
} from '../utils/collectionLanguage';

// Shared hook that reads and writes the canonical search state to the URL.
// Both SearchPage and SearchSidebarBound use this so they stay in sync without
// needing a separate context provider.
//
// Collection-specific URL params (ignored on search/cards routes):
//   collHole=1  – show catalog cards missing below the target quantity
//   collGoal=1|3 – target copies per card (default 3 when collHole is active)
//   collLang=en|zh|ko – count one printed language (absent means every copy)
//   justScanned=1 – filter owned grid to the last scan session

export type { CollectionLanguageScope };
export type CollectionViewMode = 'owned' | 'missing' | 'playset';

function parsePriceBound(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
}

function writePriceBound(params: URLSearchParams, key: string, value: number | undefined) {
  if (value == null) params.delete(key);
  else params.set(key, String(value));
}

export function collectionViewMode(holeOnly: boolean, goal: 1 | 3): CollectionViewMode {
  if (!holeOnly) return 'owned';
  return goal === 1 ? 'missing' : 'playset';
}

export function useSearchState() {
  const [params, setParams] = useSearchParams();
  const typeParam = params.get('type') ?? '';
  const supertypeParam = params.get('supertype') ?? '';
  const regionParam = params.get('region') ?? '';
  const championParam = params.get('champion') ?? '';
  const keywordParam = params.get('keyword') ?? '';
  const subtypeParam = params.get('subtype') ?? '';
  const setParam = params.get('set') ?? '';
  const rarityParam = params.get('rarity') ?? '';
  const domainParam = params.get('domain') ?? '';
  const artistParam = params.get('artist') ?? '';
  const variantParam = params.get('variant') ?? '';
  const costParam = params.get('cost') ?? '';
  const mightParam = params.get('might') ?? '';
  const powerParam = params.get('power') ?? '';
  const previewedParam = params.get('previewed') ?? '';
  const priceMinParam = params.get('priceMin');
  const priceMaxParam = params.get('priceMax');

  const filter = useMemo<RiftboundFilter>(
    () => ({
      ...emptyFilter,
      cardType: typeParam.split(',').filter(Boolean),
      supertype: supertypeParam.split(',').filter(Boolean),
      region: regionParam.split(',').filter(Boolean),
      champion: championParam.split(',').filter(Boolean),
      keyword: keywordParam.split(',').filter(Boolean),
      subtype: subtypeParam.split(',').filter(Boolean),
      set: setParam.split(',').filter(Boolean),
      rarity: rarityParam.split(',').filter(Boolean),
      domain: domainParam.split(',').filter(Boolean),
      artist: artistParam.split(',').filter(Boolean),
      variant: variantParam.split(',').filter(Boolean),
      cost:
        costParam.split(',').filter(Boolean).map(Number).filter((n) => !isNaN(n)),
      might:
        mightParam.split(',').filter(Boolean).map(Number).filter((n) => !isNaN(n)),
      power:
        powerParam.split(',').filter(Boolean).map(Number).filter((n) => !isNaN(n)),
      includePreviewed: previewedParam !== 'false',
      priceMin: parsePriceBound(priceMinParam),
      priceMax: parsePriceBound(priceMaxParam),
    }),
    [
      typeParam,
      supertypeParam,
      regionParam,
      championParam,
      keywordParam,
      subtypeParam,
      setParam,
      rarityParam,
      domainParam,
      artistParam,
      variantParam,
      costParam,
      mightParam,
      powerParam,
      previewedParam,
      priceMinParam,
      priceMaxParam,
    ]
  );

  const searchTerm = params.get('q') || '';
  const sort = params.get('sort') || DEFAULT_GRID_SORT;

  const collectionHoleOnly = params.get('collHole') === '1';
  const collectionGoal: 1 | 3 = params.get('collGoal') === '1' ? 1 : 3;
  const collectionLanguage = parseCollectionLanguage(params.get('collLang'));
  const justScanned = params.get('justScanned') === '1';

  const setFilter = useCallback(
    (next: RiftboundFilter) => {
      const np = new URLSearchParams(params);
      const apply = (key: string, value: string[]) => {
        if (value.length) np.set(key, value.join(','));
        else np.delete(key);
      };
      apply('type', next.cardType);
      apply('supertype', next.supertype);
      apply('region', next.region);
      apply('champion', next.champion);
      apply('keyword', next.keyword);
      apply('subtype', next.subtype);
      apply('set', next.set);
      apply('rarity', next.rarity);
      apply('domain', next.domain);
      apply('artist', next.artist);
      apply('variant', next.variant ?? []);
      apply('cost', next.cost.map(String));
      apply('might', next.might.map(String));
      apply('power', (next.power ?? []).map(String));
      if (next.includePreviewed === false) np.set('previewed', 'false');
      else np.delete('previewed');
      writePriceBound(np, 'priceMin', next.priceMin);
      writePriceBound(np, 'priceMax', next.priceMax);
      setParams(np, { replace: true });
      trackEvent(AnalyticsEvents.SEARCH, { search_term: params.get('q') || '' });
    },
    [params, setParams]
  );

  const setSearchTerm = useCallback(
    (value: string) => {
      const np = new URLSearchParams(params);
      if (value) np.set('q', value);
      else np.delete('q');
      setParams(np, { replace: true });
      trackEvent(AnalyticsEvents.SEARCH, { search_term: value });
    },
    [params, setParams]
  );

  const setSort = useCallback(
    (value: string) => {
      const np = new URLSearchParams(params);
      if (value && value !== DEFAULT_GRID_SORT) np.set('sort', value);
      else np.delete('sort');
      setParams(np, { replace: true });
    },
    [params, setParams]
  );

  const setCollectionHoleOnly = useCallback(
    (on: boolean) => {
      const np = new URLSearchParams(params);
      if (on) np.set('collHole', '1');
      else { np.delete('collHole'); np.delete('collGoal'); }
      setParams(np, { replace: true });
    },
    [params, setParams],
  );

  const setCollectionGoal = useCallback(
    (goal: 1 | 3) => {
      const np = new URLSearchParams(params);
      if (goal === 1) np.set('collGoal', '1');
      else np.delete('collGoal');
      setParams(np, { replace: true });
    },
    [params, setParams],
  );

  const setCollectionView = useCallback(
    (mode: CollectionViewMode) => {
      const np = new URLSearchParams(params);
      if (mode === 'owned') {
        np.delete('collHole');
        np.delete('collGoal');
      } else {
        np.set('collHole', '1');
        if (mode === 'missing') np.set('collGoal', '1');
        else np.delete('collGoal');
      }
      setParams(np, { replace: true });
    },
    [params, setParams],
  );

  const setCollectionLanguage = useCallback(
    (scope: CollectionLanguageScope) => {
      const np = new URLSearchParams(params);
      if (scope === 'all') np.delete('collLang');
      else np.set('collLang', scope);
      setParams(np, { replace: true });
    },
    [params, setParams],
  );

  const setJustScanned = useCallback(
    (on: boolean) => {
      const np = new URLSearchParams(params);
      if (on) np.set('justScanned', '1');
      else np.delete('justScanned');
      setParams(np, { replace: true });
    },
    [params, setParams],
  );

  return {
    filter,
    searchTerm,
    sort,
    setFilter,
    setSearchTerm,
    setSort,
    collectionHoleOnly,
    collectionGoal,
    collectionLanguage,
    setCollectionHoleOnly,
    setCollectionGoal,
    setCollectionView,
    setCollectionLanguage,
    justScanned,
    setJustScanned,
  };
}
